import { useAuth, useSignInLink } from '@youversion/auth';
import { Box, Divider, Heading, Text } from '@youversion/design-system-components';
import ChevronRightStroked from '@youversion/design-system-icons/lib/ChevronRightStroked';
import Modal from 'components/ui/modal';
import ModalHeader from 'components/ui/modal/Header';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { authLocale } from 'utils/helpers/authLocale';

import GiveOptionListItem from '../controls/GiveOptionListItem';

interface Props {
  onClose: () => void;
}

export default function MyGivingDialog({ onClose }: Props) {
  const { i18n, t } = useTranslation('common');
  const { language: currentLanguage } = i18n;
  const router = useRouter();
  const { user } = useAuth();
  const signInLink = useSignInLink(`${window.origin}/authenticate?from=${encodeURIComponent(router.asPath)}`, {
    appRef: 'portal',
    locale: authLocale(currentLanguage),
  });
  const redirectToSignIn = useCallback(() => {
    if (window.FlutterAuthRoute) {
      window.FlutterAuthRoute.postMessage('');
    } else {
      window.location.href = signInLink;
    }
  }, [signInLink]);

  useEffect(() => void firebaseLogEvent('GivingScreenView', { screen: 'giving_my_giving' }), []);

  return (
    <Modal onClose={onClose}>
      <Box className='flex flex-col h-full'>
        <ModalHeader
          left={
            <Box
              className='flex w-fit cursor-pointer'
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Cancel', screen: 'giving_my_giving' });
                onClose();
              }}
            >
              <NextLink className='flex w-fit' href='/give?hide_nav=true'>
                <Text>{t('giving.cta.cancel')}</Text>
              </NextLink>
            </Box>
          }
          title={t('giving.navigation.my_giving')}
        />
        <Divider className='border-t-none border-b-small' />
        <Box className='m-2'>
          {user ? (
            <Box className=''>
              <Heading className='mis-1' level={3} variant='caption'>
                {t('giving.navigation.my_giving')}
              </Heading>
              <NextLink
                href='/history?hide_nav=true'
                onClick={() => {
                  firebaseLogEvent('GivingButtonTap', { label: 'Giving History', screen: 'giving_my_giving' });
                }}
              >
                <GiveOptionListItem
                  label={t('giving.titles.giving_history')}
                  rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
                  secondaryLabel={t('giving.messages.view_history')}
                />
              </NextLink>
              <NextLink
                href='/schedule?hide_nav=true'
                onClick={() => {
                  firebaseLogEvent('GivingButtonTap', { label: 'Manage Giving', screen: 'giving_my_giving' });
                }}
              >
                <GiveOptionListItem
                  label={t('giving.titles.scheduled_giving')}
                  rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
                  secondaryLabel={t('giving.messages.manage_gifts')}
                />
              </NextLink>
            </Box>
          ) : (
            <Box className='mbs-3 justify-center flex'>
              <button
                className='min-w-full plb-1 flex items-center justify-center rounded-4 text-text-dark bg-canvas-dark dark:bg-canvas-secondary-dark'
                onClick={async () => {
                  await firebaseLogEvent('GivingButtonTap', { label: 'Sign In', screen: 'giving_my_giving' });
                  redirectToSignIn();
                }}
              >
                {t('giving.titles.sign_in')}
              </button>
            </Box>
          )}
          <Box className='mbs-2'>
            <Heading className='mis-1' level={3} variant='caption'>
              {t('giving.footer.help')}
            </Heading>
            <Box className='mlb-1'>
              <NextLink
                href='https://help.youversion.com/l/en/category/137udd78cx-giving'
                onClick={() =>
                  firebaseLogEvent('GivingButtonTap', { label: 'Giving FAQs', screen: 'giving_my_giving' })
                }
                target='_blank'
              >
                <GiveOptionListItem
                  label={t('giving.titles.givings_faqs')}
                  rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
                />
              </NextLink>
              <NextLink
                href='https://help.youversion.com'
                onClick={() =>
                  firebaseLogEvent('GivingButtonTap', { label: 'Email Giving Support', screen: 'giving_my_giving' })
                }
              >
                <GiveOptionListItem
                  label={t('giving.titles.email_support')}
                  rightElement={<ChevronRightStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />}
                />
              </NextLink>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
